// The screens the /tutorial-demo player animates. They reuse the real app's class names and
// stylesheets (groups/index.scss, OnboardingWizard.scss, SupportWidget.scss, Metronic) with
// dummy data, so the demo looks like the product without touching the API. Layout helpers
// that depend on viewport breakpoints (d-lg-*, w-lg-*) are replaced by fixed inline styles —
// the stage is a fixed 1280×800 canvas regardless of the viewer's screen.
import React from 'react'
import clsx from 'clsx'
import {
  DemoState,
  DEMO_PROFILES,
  DEMO_POST_MEDIA,
  DEMO_GROUPS,
  DEMO_LIBRARY_GROUPS,
  DEMO_SCHEDULE,
  DEMO_CAMPAIGN_NAME,
  DEMO_CAMPAIGNS,
  DEMO_POST_TEXT,
  DEMO_MP,
  DEMO_POST_REPLY,
} from './demoScript'

const FbLogo = ({size = 40}: {size?: number}) => (
  <svg viewBox='0 0 36 36' height={size} width={size}>
    <defs>
      <linearGradient id='tdFbGrad' x1='50%' x2='50%' y1='97.078%' y2='0%'>
        <stop offset='0%' stopColor='#0062E0' />
        <stop offset='100%' stopColor='#19AFFF' />
      </linearGradient>
    </defs>
    <path fill='url(#tdFbGrad)' d='M15 35.8C6.5 34.3 0 26.9 0 18 0 8.1 8.1 0 18 0s18 8.1 18 18c0 8.9-6.5 16.3-15 17.8l-1-.8h-4l-1 .8z' />
    <path fill='#fff' d='M25 23l.8-5H21v-3.5c0-1.4.5-2.5 2.7-2.5H26V7.4c-1.3-.2-2.7-.4-4-.4-4.1 0-7 2.5-7 7v4h-4.5v5H15v12.7c1 .2 2 .3 3 .3s2-.1 3-.3V23h4z' />
  </svg>
)

const Check = ({color = '#fff', size = 10}: {color?: string; size?: number}) => (
  <svg width={size} height={size} viewBox='0 0 24 24' fill='none'>
    <path d='M5 12l5 5L20 7' stroke={color} strokeWidth='3' strokeLinecap='round' strokeLinejoin='round' />
  </svg>
)

// ── Login (AuthLayout + Login.tsx) ────────────────────────────────────────────
export const LoginScreen: React.FC<{s: DemoState}> = ({s}) => (
  <div className='td-login' style={{direction: 'rtl', display: 'flex', height: '100%', backgroundImage: 'url(/media/misc/auth-bg.png)', backgroundSize: 'cover', backgroundPosition: 'center'}}>
    <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40}}>
      <div data-td='login-card' className='p-10 bg-body rounded-3 shadow-sm' style={{width: 500}}>
        <form className='form w-100' onSubmit={(e) => e.preventDefault()}>
          <div className='text-center mb-11'>
            <h1 className='text-dark fw-bolder mb-3'>התחברות</h1>
          </div>
          <div className='d-flex mb-8'>
            <button type='button' className='btn btn-sm flex-grow-1 me-2 btn-primary'>שם משתמש וסיסמה</button>
            <button type='button' className='btn btn-sm flex-grow-1 btn-light'>כניסה עם טלפון</button>
          </div>
          <div className='fv-row mb-8'>
            <label className='form-label fs-6 fw-bolder text-dark'>שם משתמש</label>
            <input data-td='login-user' readOnly placeholder='שם משתמש' value={s.loginUser} className={clsx('form-control bg-transparent', s.loginUser && 'td-focus')} type='text' dir='ltr' />
          </div>
          <div className='fv-row mb-3'>
            <label className='form-label fw-bolder text-dark fs-6 mb-0'>סיסמה</label>
            <input data-td='login-pass' readOnly type='password' placeholder='סיסמה' value={s.loginPass} className={clsx('form-control bg-transparent', s.loginPass && 'td-focus')} />
          </div>
          <div className='d-grid mb-10 mt-8'>
            <button data-td='login-submit' type='button' className='btn btn-primary'>
              {!s.loginLoading && <span className='indicator-label'>המשך</span>}
              {s.loginLoading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  מתחבר...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
    <div style={{flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
      <img style={{width: 400}} src='/media/logos/d-code-logo-light.svg' alt='' />
    </div>
  </div>
)

// ── App shell ─────────────────────────────────────────────────────────────────
const MENU = [
  {icon: 'bi-megaphone', label: 'קמפיינים', active: true},
  {icon: 'bi-calendar-week', label: 'תזמונים'},
  {icon: 'bi-bar-chart', label: 'סטטיסטיקה'},
  {icon: 'bi-shop', label: 'מרקטפלייס'},
  {icon: 'bi-telephone', label: 'מספר וירטואלי'},
  {icon: 'bi-gear', label: 'הגדרות'},
]

export const AppScreen: React.FC<{s: DemoState; contentRef: React.RefObject<HTMLDivElement>}> = ({s, contentRef}) => (
  <div className='td-app' style={{direction: 'rtl'}}>
    <aside className='td-sidebar'>
      <div className='td-sidebar-logo'>
        <img src='/media/logos/d-code-logo-light.svg' alt='' />
      </div>
      {MENU.map((m) => (
        <div key={m.label} data-td={m.label === 'תזמונים' ? 'menu-schedule' : undefined} className={clsx('td-menu-item', (s.view === 'schedule' ? m.label === 'תזמונים' : m.active) && 'active')}>
          <i className={`bi ${m.icon}`}></i>
          <span>{m.label}</span>
        </div>
      ))}
    </aside>
    <div className='td-main'>
      <header className='td-header'>
        <div className='td-header-title'>{s.view === 'schedule' ? 'תזמונים' : 'קמפיינים'}</div>
        <div className='td-header-user'>
          <span>שלום, דוד</span>
          <div className='td-header-avatar'>ד</div>
        </div>
      </header>
      <div className='td-app-content' ref={contentRef}>
        {s.view === 'schedule' ? <ScheduleView s={s} /> : <CampaignsView s={s} />}
      </div>
    </div>

    {/* SupportWidget */}
    <div className='support-widget' style={{position: 'absolute'}}>
      {s.supportOpen && (
        <div className='support-widget-panel td-support-panel'>
          <div className='support-widget-header'>
            <div>
              <h5>מרכז התמיכה</h5>
              <span className='support-widget-subtitle'>נשמח לעזור!</span>
            </div>
          </div>
          <div className='td-support-body'>
            <div className='td-support-row'><i className='bi bi-chat-dots'></i> פתיחת פנייה חדשה</div>
            <div className='td-support-row'><i className='bi bi-whatsapp' style={{color: '#25d366'}}></i> שיחה בוואטסאפ</div>
            <div className='td-support-row'><i className='bi bi-play-circle'></i> סרטוני הדרכה</div>
          </div>
        </div>
      )}
      <button data-td='support-btn' className={clsx('support-widget-button', s.supportOpen && 'active')} title='תמיכה'>
        {s.supportOpen ? <i className='bi bi-x-lg'></i> : <i className='bi bi-headset'></i>}
      </button>
    </div>

    {s.campaignModal && <CampaignModal s={s} />}
    {s.postModal && <PostModal s={s} />}
    {s.groupsModal && <GroupsModal s={s} />}
    {s.connectModal && <ConnectModal s={s} />}
    {s.browserOpen && <FacebookBrowser s={s} />}
    {s.phone !== 'off' && <PhoneMock s={s} />}
  </div>
)

// ── Campaigns page (GroupsWrapper) ────────────────────────────────────────────
const CampaignsView: React.FC<{s: DemoState}> = ({s}) => {
  const [me, page] = DEMO_PROFILES
  const warning = !s.hasPost || s.groupCount === 0
  const enabled = s.campaignEnabled && !warning
  return (
    <>
      {/* Profiles */}
      <div className='card mb-6' style={{direction: 'rtl'}}>
        <div className='card-body pt-6 pb-6'>
          <div className='profiles-header'>
            <h2>פרופילים</h2>
            <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
              <span style={{width: 8, height: 8, borderRadius: '50%', background: '#4caf50', display: 'inline-block'}}></span>
              <span style={{fontSize: 12, color: '#666'}}>מחובר</span>
            </div>
          </div>
          <div className='profile-cards-container'>
            <div data-td='profile-card' className={clsx('profile-card', s.connected ? 'connected' : 'needs-attention')}>
              <div className='profile-card-main'>
                <div className='profile-card-avatar'>
                  <img src={me.avatar} alt='' />
                  <span className={`profile-status-indicator ${s.connected ? 'connected' : 'disconnected'}`}></span>
                </div>
                <div className='profile-card-info'>
                  <div className='profile-name'>{me.name}</div>
                  <div className={`profile-status ${s.connected ? 'connected' : 'disconnected'}`}>{s.connected ? '● מחובר' : '○ לא מחובר'}</div>
                </div>
                <div className='profile-card-action' style={{display: 'flex', alignItems: 'center', gap: 4}}>
                  {s.connected ? (
                    <button style={{background: 'none', border: 'none', fontSize: 20, padding: '4px 8px', color: '#65676b', lineHeight: 1}}>⋮</button>
                  ) : (
                    <button data-td='connect-btn' className='btn-connect'>התחבר</button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Campaigns */}
      <div data-td='campaigns-card' className='card mb-6' style={{direction: 'rtl'}}>
        <div className='card-body pt-6 pb-6'>
          <div className='campaigns-header'>
            <h2>
              קמפיינים
              {s.hasCampaign && <span style={{fontWeight: 400, fontSize: 16, color: '#666', marginRight: 8}}>/ {DEMO_CAMPAIGNS[s.activeCamp] || DEMO_CAMPAIGN_NAME}</span>}
            </h2>
            <button data-td='new-campaign' className='new-campaign-btn td-new-campaign'>➕ קמפיין חדש</button>
          </div>
          <div className='campaigns-cards-container'>
            {!s.hasCampaign ? (
              <div className='empty-campaigns-state'>
                <div className='empty-campaigns-icon'>📢</div>
                <h3 className='empty-campaigns-title'>אין קמפיינים עדיין</h3>
                <p className='empty-campaigns-text'>צור את הקמפיין הראשון שלך והתחל לפרסם בקבוצות פייסבוק</p>
                <button className='empty-campaigns-btn'>➕ צור קמפיין ראשון</button>
              </div>
            ) : s.extraCampaigns ? (
              DEMO_CAMPAIGNS.map((name, i) => (
                <div key={name} data-td={`camp-card-${i}`} className={clsx('campaign-card', i === s.activeCamp && 'active')}>
                  <div className='campaign-card-header' style={{marginBottom: 6}}>
                    <span className='campaign-status-dot active'></span>
                    <div className='campaign-name'>קמפיין - {name}</div>
                  </div>
                  <div className='campaign-stats'>
                    <span className='clickable-stat'>📊 {i === 0 && s.hasPost ? 1 : 0} פוסטים</span>
                    <span className='clickable-stat'>👥 {s.groupCount} קבוצות</span>
                  </div>
                </div>
              ))
            ) : (
              <div data-td='campaign-card' className={clsx('campaign-card active td-pop', (!enabled) && 'paused')} style={{gridColumn: 'span 2'}}>
                <div style={{display: 'flex', alignItems: 'center', width: '100%', gap: 12}}>
                  <div className='campaign-card-header' style={{flex: 1, marginBottom: 0}}>
                    <span className={`campaign-status-dot ${warning ? 'no-groups' : enabled ? 'active' : 'paused'}`}></span>
                    <div className='campaign-name'>קמפיין - {DEMO_CAMPAIGN_NAME}</div>
                  </div>
                  <div className='campaign-card-actions'>
                    <label className='campaign-toggle' data-td='campaign-toggle'>
                      <input type='checkbox' readOnly checked={enabled} />
                      <span className='campaign-toggle-slider'></span>
                    </label>
                    <button className='camp-btn edit'>✏️</button>
                    <button className='camp-btn delete'>🗑</button>
                    <div className='campaign-menu-wrap'>
                      <button className='camp-btn more'>⋯</button>
                    </div>
                  </div>
                </div>
                <div className='campaign-card-info'>
                  <div className='campaign-profiles-row' style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                    <span style={{fontSize: 13, color: '#666', fontWeight: 500}}>בחר את הפרופילים שיפרסמו בקמפיין זה</span>
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                      {[me, page].map((p) => (
                        <div key={p.name} className='profile-avatar-with-tooltip' style={{position: 'relative'}}>
                          <img src={p.avatar} alt='' style={{width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', border: '3px solid #1877f2'}} />
                          <span style={{position: 'absolute', bottom: 0, right: 0, width: 10, height: 10, borderRadius: '50%', backgroundColor: s.connected ? '#22c55e' : '#ef4444', border: '2px solid #fff'}}></span>
                          <span style={{position: 'absolute', top: -4, left: -4, width: 16, height: 16, borderRadius: '50%', backgroundColor: '#1877f2', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                            <Check />
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className='campaign-stats' style={{marginTop: 10}}>
                    <span data-td='posts-stat' className='clickable-stat'>📊 {s.hasPost ? 1 : 0} פוסטים</span>
                    <span data-td='groups-stat' className='clickable-stat'>👥 {s.groupCount} קבוצות</span>
                    <span data-td='published-stat' className='clickable-stat'>📈 {s.published.toLocaleString()} פורסמו</span>
                    <span data-td='today-stat'>📅 {s.today} היום</span>
                  </div>
                  {s.groupCount === 0 && (
                    <div className='no-groups-warning' style={{marginTop: 8, padding: '8px 12px', background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', borderRadius: 8, fontSize: 12, color: '#c62828', lineHeight: 1.5, border: '1px solid #ef9a9a'}}>
                      <span style={{marginLeft: 6}}>⚠️</span>
                      <strong>שים לב חובה לבחור קבוצות:</strong> לא נבחרו קבוצות לקמפיין זה.
                    </div>
                  )}
                  {!s.hasPost && (
                    <div className='no-posts-warning' style={{marginTop: 8, padding: '8px 12px', background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)', borderRadius: 8, fontSize: 12, color: '#c62828', lineHeight: 1.5, border: '1px solid #ef9a9a'}}>
                      <span style={{marginLeft: 6}}>⚠️</span>
                      <strong>שים לב חובה להוסיף פוסטים:</strong> אין פוסטים בקמפיין זה.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Posts */}
      {s.hasCampaign && (
        <div className='card mb-6'>
          <div className='card-body'>
            <div data-td='posts-section' id='td-posts-section' className='posts-section' style={{direction: 'rtl'}}>
              <div className='posts-grid'>
                {!s.hasPost || s.activeCamp !== 0 ? (
                  <div className='empty-posts-state'>
                    <div className='empty-posts-icon'>📝</div>
                    <h3 className='empty-posts-title'>אין פוסטים עדיין בקמפיין {DEMO_CAMPAIGNS[s.activeCamp] || DEMO_CAMPAIGN_NAME}</h3>
                    <p className='empty-posts-text'>צור את הפוסט הראשון שלך וזה יפורסם אוטומטית בקבוצות שבחרת</p>
                    <button data-td='add-post' className='empty-posts-btn'>➕ צור פוסט ראשון</button>
                  </div>
                ) : (
                  <>
                    <button data-td='add-post-card' className='add-post-card'>
                      <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' style={{flexShrink: 0}}>
                        <line x1='12' y1='5' x2='12' y2='19'></line>
                        <line x1='5' y1='12' x2='19' y2='12'></line>
                      </svg>
                      <span>לחץ כאן להוספת פוסט לקמפיין {DEMO_CAMPAIGN_NAME}</span>
                    </button>
                    <PostCard />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const PostCard: React.FC = () => (
  <div data-td='post-card' className='fb-post-card td-pop'>
    <div className='fb-post-header'>
      <div className='fb-post-author'>
        <img className='fb-author-avatar' src={DEMO_PROFILES[0].avatar} alt='' />
        <div className='fb-author-info'>
          <div className='fb-author-name'>{DEMO_PROFILES[0].name}</div>
        </div>
      </div>
      <div className='fb-post-actions'>
        <span className='td-mp-badge'>🛒 מרקטפלייס</span>
        <button className='fb-edit-btn'>✏️ עריכה</button>
        <button style={{background: 'none', border: 'none', fontSize: 20, padding: '4px 8px', color: '#65676b', lineHeight: 1}}>⋯</button>
      </div>
    </div>
    <div className='fb-post-section fb-post-content-wrapper'>
      <div className='fb-post-content clamped' style={{whiteSpace: 'pre-line'}}>{DEMO_POST_TEXT}</div>
    </div>
    <div className='fb-post-section fb-post-media-wrapper'>
      <div className='fb-post-media multi-image'>
        {DEMO_POST_MEDIA.map((m) => (
          <div key={m} className='fb-media-item'>
            <img src={m} alt='' />
          </div>
        ))}
      </div>
    </div>
  </div>
)


// ── Schedule view (how the system works) ──────────────────────────────────────
const ScheduleView: React.FC<{s: DemoState}> = ({s}) => (
  <div className='card mb-6' style={{direction: 'rtl'}}>
    <div className='card-body'>
      <div className='d-flex align-items-center justify-content-between mb-5'>
        <h2 className='m-0 fw-bolder fs-3'>התזמונים של היום</h2>
        <span className='badge badge-light-primary fs-7 fw-bold'>{s.scheduleDone} / {DEMO_SCHEDULE.length} פורסמו</span>
      </div>
      <div className='td-schedule'>
        {DEMO_SCHEDULE.map((row, i) => {
          const status = i < s.scheduleDone ? 'done' : i === s.scheduleDone ? 'running' : 'pending'
          return (
            <div key={i} data-td={`sched-${i}`} className={clsx('td-sched-row', status)}>
              <div className='td-sched-time'>{row.time}</div>
              <img className='td-sched-avatar' src={DEMO_PROFILES[row.profile].avatar} alt='' />
              <div className='td-sched-info'>
                <div className='td-sched-group'>{DEMO_GROUPS[row.group].name}</div>
                <div className='td-sched-meta'>{DEMO_PROFILES[row.profile].name} · קמפיין {DEMO_CAMPAIGN_NAME}</div>
              </div>
              <div className='td-sched-status'>
                {status === 'done' && <span className='badge badge-light-success'>✓ פורסם</span>}
                {status === 'running' && (
                  <span className='badge badge-light-warning'>
                    <span className='spinner-border spinner-border-sm me-1' style={{width: 10, height: 10}}></span> מפרסם...
                  </span>
                )}
                {status === 'pending' && <span className='badge badge-light'>מתוזמן</span>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  </div>
)

// ── Create campaign modal (#kt_modal_1) ───────────────────────────────────────
const CampaignModal: React.FC<{s: DemoState}> = ({s}) => (
  <>
    <div className='modal fade show' style={{display: 'block'}}>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content td-modal-in'>
          <div className='modal-header'>
            <h5 className='modal-title'>יצירת קמפיין</h5>
            <div className='btn btn-icon btn-sm btn-active-light-primary ms-2'>
              <i className='bi bi-x-lg fs-3'></i>
            </div>
          </div>
          <div className='modal-body'>
            <div style={{marginBottom: 20, padding: '12px 16px', backgroundColor: '#f8f9fa', borderRadius: 8, fontSize: 13, color: '#555', lineHeight: 1.6}}>
              <p style={{margin: '0 0 8px 0'}}>קמפיין זו תיקייה שמארגנת את הפוסטים שלכם לפי אזור או תחום.</p>
              <p style={{margin: '0 0 8px 0'}}>יש ליצור קמפיין נפרד לכל אזור או קטגוריה.</p>
              <p style={{margin: 0, fontWeight: 500}}>שם הקמפיין לא מוצג בפייסבוק – הוא מיועד רק לניהול וסדר פנימי.</p>
            </div>
            <div className='form-group' style={{marginBottom: 20}}>
              <label style={{fontWeight: 600, marginBottom: 8, display: 'block'}}>שם הקמפיין</label>
              <input data-td='camp-name' readOnly type='text' value={s.campName} className={clsx('form-control bg-transparent', s.campName && 'td-focus')} placeholder='לדוגמא: נכסים תל אביב' />
            </div>
            <div className='form-group' data-td='camp-profiles'>
              <label style={{marginBottom: 8, display: 'block'}}>בחר פרופילים לקמפיין</label>
              <p style={{fontSize: 13, color: '#666', marginBottom: 12}}>
                בחר את הפרופילים שיפרסמו בקמפיין זה ({s.campProfiles.length} / {DEMO_PROFILES.length} נבחרו)
              </p>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 8, padding: 4}}>
                {DEMO_PROFILES.map((p, i) => {
                  const selected = s.campProfiles.includes(i)
                  return (
                    <div key={p.name} data-td={`camp-profile-${i}`} style={{display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 8, border: selected ? '2px solid #1877f2' : '1px solid #e0e0e0', backgroundColor: selected ? '#e7f3ff' : '#fff', transition: 'all 0.2s'}}>
                      <img src={p.avatar} alt='' style={{width: 32, height: 32, borderRadius: '50%', objectFit: 'cover'}} />
                      <span style={{fontWeight: selected ? 600 : 400, color: selected ? '#1877f2' : '#333', fontSize: 13}}>{p.name}</span>
                      {selected && <Check color='#1877f2' size={16} />}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
          <div className='modal-footer' style={{flexDirection: 'column', alignItems: 'stretch', gap: 12}}>
            <p style={{fontSize: 12, color: '#888', margin: 0, textAlign: 'center'}}>ניתן להוסיף קמפיינים נוספים בהמשך מתוך המערכת.</p>
            <button data-td='camp-submit' type='button' className='btn btn-primary' style={{width: '100%'}}>
              {s.campCreating ? (
                <span><span className='spinner-border spinner-border-sm me-2' role='status'></span>יוצר קמפיין...</span>
              ) : 'צור קמפיין'}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div className='modal-backdrop fade show'></div>
  </>
)

// ── Create post modal (.fb-edit-modal) ────────────────────────────────────────
const PostModal: React.FC<{s: DemoState}> = ({s}) => (
  <div className='fb-edit-modal-backdrop'>
    <div className='fb-edit-modal td-modal-in' style={{direction: 'rtl'}}>
      <div className='fb-modal-header'>
        <button className='fb-modal-close'>×</button>
        <h3>יצירת פוסט</h3>
      </div>
      {s.extraCampaigns && (
        <div data-td='post-campaign' style={{margin: '10px 20px 0', padding: '8px 12px', borderRadius: 8, background: '#eef4ff', color: '#1d4ed8', fontWeight: 600, fontSize: 14}}>
          📁 לקמפיין: {DEMO_CAMPAIGNS[s.activeCamp]}
        </div>
      )}
      <div className='fb-modal-body'>
        <div className='fb-modal-field' data-field='name'>
          <label>שם הפוסט (לא חובה)</label>
          <input data-td='post-name' readOnly type='text' placeholder='תן שם לפוסט שלך...' value={s.postName} className={clsx(s.postName && 'td-focus')} />
        </div>

        <div className='fb-modal-field' data-field='content'>
          <label><span>תוכן הפוסט</span></label>
          <div className='fb-textarea-wrapper'>
            <button data-td='post-ai' type='button' className='fb-ai-enhance-btn' disabled={!s.postContent}>
              {s.postAi ? (<><span className='spinner-border spinner-border-sm me-2'></span>משפר...</>) : '✨ שפר עם AI'}
            </button>
            <textarea data-td='post-content' readOnly value={s.postContent} placeholder='כתבו כאן את תוכן המודעה...' className={clsx(s.postContent && 'td-focus', s.postAi && 'td-ai-glow')} style={{minHeight: 170}} />
          </div>
          <div className='fb-post-content-actions'>
            <span className='fb-field-hint'>💡 טיפ: לא לשכוח להשאיר מספר טלפון, לינק לאתר או קישור לוואטסאפ בתוכן הפוסט כדי שיוכלו ליצור איתך קשר</span>
          </div>
        </div>

        <div className='fb-modal-field' data-field='post-type' style={{marginBottom: 12}}>
          <label style={{display: 'block', fontSize: 13, fontWeight: 600, color: '#65676b', marginBottom: 8}}>סוג הפוסט</label>
          <div style={{display: 'flex', gap: 8}}>
            {['🖼️ אלבום (תמונות)', '🎨 טקסט + רקע (ללא תמונות)'].map((label, i) => (
              <button key={label} type='button' style={{flex: 1, padding: '10px 12px', borderRadius: 8, border: i === 0 ? '2px solid #1877f2' : '2px solid #ddd', background: i === 0 ? '#e7f3ff' : '#fff', color: i === 0 ? '#1877f2' : '#666', fontWeight: 600, fontSize: 13}}>
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className='fb-modal-media' data-field='media'>
          <label style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: '#65676b', marginBottom: 8}}>
            <span>תמונות / סרטונים ({s.postMedia})</span>
          </label>
          {s.postMedia > 0 && (
            <div className='fb-media-preview'>
              {DEMO_POST_MEDIA.slice(0, s.postMedia).map((m, i) => (
                <div key={m} className='fb-media-item td-pop'>
                  <img src={m} alt='' />
                  <span className='fb-media-number'>{i + 1}</span>
                </div>
              ))}
            </div>
          )}
          <div data-td='post-dropzone' className='td-dropzone'>
            <i className='bi bi-cloud-arrow-up fs-2'></i>
            <p>גררו לכאן תמונות או סרטונים, או לחצו לבחירה</p>
          </div>
        </div>

        <div className='fb-modal-field' data-field='reply' style={{marginTop: 14}}>
          <label style={{display: 'flex', alignItems: 'center', gap: 8}}><span>תגובה אוטומטית</span></label>
          <span className='fb-form-hint' style={{marginBottom: 8, display: 'block', fontWeight: 'bold'}}>
            אם תמלאו את שדה "תגובה אוטומטית", המערכת תתייג כל מי שהגיב לפוסט ותשלח לו את התגובה שהגדרתם – באופן אוטומטי.
          </span>
          <div className='fb-textarea-wrapper'>
            <button type='button' className='fb-ai-enhance-btn'>✨ צור עם AI</button>
            <textarea data-td='post-reply' readOnly value={s.postReply} placeholder='לדוגמה: היי בבקשה, לפרטים נוספים השאירו הודעה בווצאפ...' className={clsx(s.postReply && 'td-focus')} style={{minHeight: 80}} />
          </div>
        </div>

        <MarketplaceSection s={s} />
      </div>
      <div className='fb-modal-footer'>
        <button className='fb-btn fb-btn-secondary'>ביטול</button>
        <button data-td='post-publish' className='fb-btn fb-btn-primary'>{s.postSaving ? 'שומר...' : 'פרסם'}</button>
      </div>
    </div>
  </div>
)

// Marketplace block of the post modal: checkbox → AI fill → listing fields (home_sale).
const MarketplaceSection: React.FC<{s: DemoState}> = ({s}) => (
  <div className='fb-marketplace-section' data-field='marketplace' data-td='mp-section'>
    <label className='fb-marketplace-toggle' data-td='mp-toggle'>
      <input type='checkbox' readOnly checked={s.mpEnabled} />
      <span>🛒 סמנו כאן כדי לפרסם גם במרקטפלייס</span>
    </label>
    {!s.mpEnabled && (
      <div style={{fontSize: 12, color: '#6b7280', marginTop: 4, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4}}>
        ℹ️ יש לסמן את התיבה למעלה כדי שהפוסט יתפרסם גם במרקטפלייס
      </div>
    )}

    {s.mpFilling && (
      <div className='td-pop' style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderRadius: 12, marginTop: 16}}>
        <div style={{width: 50, height: 50, border: '4px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: 16}}></div>
        <span style={{fontSize: 16, fontWeight: 600, color: '#1e40af'}}>🤖 ממלא פרטי מרקטפלייס אוטומטית...</span>
        <span style={{fontSize: 13, color: '#3b82f6', marginTop: 8}}>מנתח את תוכן הפוסט ומזהה סוג, מחיר וקטגוריה</span>
      </div>
    )}

    {s.mpFilled && (
      <div className='fb-marketplace-options td-pop'>
        <div className='fb-modal-field'>
          <label style={{display: 'flex', alignItems: 'center', gap: 8}}>
            <span>פרופילים לפרסום *</span>
            <span style={{color: '#65676b', fontWeight: 400, fontSize: 12}}>(1/1)</span>
          </label>
          <div className='fb-profile-circles'>
            <button type='button' className='fb-profile-circle selected'>
              <span className='fb-profile-circle-img'>
                <img src={DEMO_PROFILES[0].avatar} alt='' />
                <i className='fb-profile-circle-dot online'></i>
                <i className='bi bi-check-lg fb-profile-circle-check'></i>
              </span>
              <span className='fb-profile-circle-name'>דוד</span>
            </button>
          </div>
        </div>

        <div className='fb-modal-field' data-td='mp-type'>
          <label>סוג מודעה</label>
          <div className='fb-listing-types'>
            {[
              {value: 'item', label: '📦 פריט למכירה'},
              {value: 'vehicle', label: '🚗 רכב'},
              {value: 'home_rent', label: '🏠 דירה להשכרה'},
              {value: 'home_sale', label: '🏡 דירה למכירה'},
            ].map((t) => (
              <button key={t.value} type='button' className={clsx('fb-listing-type-btn', DEMO_MP.listingType === t.value && 'active')}>{t.label}</button>
            ))}
          </div>
        </div>

        <div className='fb-modal-field' data-field='mp-price' data-td='mp-price'>
          <label>מחיר (₪) *</label>
          <input readOnly type='text' value={DEMO_MP.price} className='td-ai-filled' />
        </div>

        <div className='fb-modal-field'>
          <label>תיאור</label>
          <textarea readOnly value={DEMO_MP.description} className='td-ai-filled' style={{minHeight: 60}} />
        </div>

        <div className='fb-modal-field'>
          <label>סוג נכס *</label>
          <select disabled value='apartment' className='td-ai-filled'>
            <option value='apartment'>{DEMO_MP.propertyType}</option>
          </select>
        </div>

        <div className='fb-form-row' data-td='mp-rooms'>
          <div className='fb-modal-field'>
            <label>חדרים *</label>
            <input readOnly type='text' value={DEMO_MP.rooms} className='td-ai-filled' />
          </div>
          <div className='fb-modal-field'>
            <label>חדרי רחצה</label>
            <input readOnly type='text' value={DEMO_MP.baths} className='td-ai-filled' />
          </div>
          <div className='fb-modal-field'>
            <label>מ"ר</label>
            <input readOnly type='text' value={DEMO_MP.size} className='td-ai-filled' />
          </div>
        </div>

        <div className='fb-modal-field' data-field='mp-location' data-td='mp-location'>
          <label>מיקום *</label>
          <input readOnly type='text' value={DEMO_MP.location} className='td-ai-filled' />
        </div>

        <span className='fb-form-hint' style={{marginTop: 12, display: 'block'}}>💡 המודעה תפורסם ישירות בפייסבוק מרקטפלייס דרך הפרופיל שנבחר</span>

        <div className='fb-modal-field' style={{marginTop: 12}}>
          <label>הודעת תגובה אוטומטית <span style={{color: '#999', fontWeight: 'normal', fontSize: 12, marginRight: 4}}>(לא חובה)</span></label>
          <textarea readOnly value={DEMO_POST_REPLY} className='td-ai-filled' style={{minHeight: 60}} />
        </div>
      </div>
    )}
  </div>
)

// ── Groups modal (#kt_modal_8 / TablesWidget10) ───────────────────────────────
const GroupsModal: React.FC<{s: DemoState}> = ({s}) => {
  const selected = s.libOn ? DEMO_LIBRARY_GROUPS - (s.removedGroup ? 1 : 0) : 0
  return (
    <>
      <div className='modal fade show' style={{display: 'block'}}>
        <div className='modal-dialog modal-xl modal-dialog-centered' style={{maxWidth: 1000}}>
          <div className='modal-content td-modal-in' style={{height: 660, overflow: 'hidden'}}>
            <div className='sg-header'>
              <div className='sg-selection-counter'>
                <div className='sg-counter-badge'>
                  <span className='sg-counter-number'>{selected}</span>
                  <span className='sg-counter-label'>קבוצות נבחרו</span>
                  <span className='sg-campaign-name'>לקמפיין: {DEMO_CAMPAIGN_NAME}</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: 8, marginRight: 'auto'}}>
                  <button className='sg-close-btn'>
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none'><path d='M18 6L6 18M6 6l12 12' stroke='currentColor' strokeWidth='2' strokeLinecap='round' /></svg>
                  </button>
                </div>
              </div>
              <div style={{padding: '6px 14px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #f3f4f6', textAlign: 'center'}}>
                הפעל/כבה ספריות קבוצות לקמפיין. לחץ על ✏️ לעריכת ספריית קבוצות.
              </div>
            </div>
            <div className='sg-content' style={{overflow: 'hidden'}}>
              <div style={{padding: '12px 14px'}}>
                {s.libLoading ? (
                  <div className='lib-card' style={{border: '1.5px dashed #86efac', background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)'}}>
                    <div className='lib-card-stripe ai'></div>
                    <div className='lib-card-body' style={{justifyContent: 'center', gap: 10}}>
                      <div style={{width: 22, height: 22, border: '2.5px solid #bbf7d0', borderTopColor: '#22c55e', borderRadius: '50%', animation: 'spin 0.8s linear infinite'}}></div>
                      <span style={{fontSize: 12, color: '#166534', fontWeight: 600}}>🤖 מחפש ספריית קבוצות מתאימה...</span>
                    </div>
                  </div>
                ) : (
                  <div className='lib-grid'>
                    <div data-td='lib-card' className={clsx('lib-card', s.libOn && 'selected')}>
                      <button className={`lib-toggle ${s.libOn ? 'on' : 'off'}`} style={{position: 'absolute', top: 8, left: 8, zIndex: 1}}>
                        <div className='lib-toggle-knob'></div>
                      </button>
                      <div className='lib-card-body'>
                        <div className='lib-card-icon ai'>🤖</div>
                        <div className='lib-card-info'>
                          <div className='lib-card-name'>נדל״ן תל אביב</div>
                          <div className='lib-card-meta'>
                            <span className='lib-card-badge' style={{background: '#dcfce7', color: '#166534'}}>אוטומטית</span>
                          </div>
                        </div>
                        <div className='lib-card-count'>
                          <span className='count-num'>{selected}</span>
                          <span className='count-label'>קבוצות</span>
                        </div>
                      </div>
                      <div className='lib-card-actions'>
                        <button className='lib-card-edit'>✏️ עריכה</button>
                      </div>
                      <div className='lib-card-bar'><div className='lib-card-bar-fill green' style={{width: `${(selected / DEMO_LIBRARY_GROUPS) * 100}%`}}></div></div>
                    </div>
                  </div>
                )}
                {!s.libLoading && (
                  <div className='sg-groups-list' style={{marginTop: 14}}>
                    {DEMO_GROUPS.slice(0, s.groupsShown).map((g, i) => {
                      const isSelected = !(i === 2 && s.removedGroup)
                      return (
                        <div key={g.name} data-td={`group-${i}`} className={clsx('sg-group-item td-pop', isSelected ? 'selected' : 'blacklisted')} style={!isSelected ? {backgroundColor: '#fef2f2', borderColor: '#fecaca'} : {}}>
                          <div className='sg-group-checkbox'>
                            {isSelected ? (
                              <svg width='22' height='22' viewBox='0 0 24 24' fill='none'><rect x='2' y='2' width='20' height='20' rx='4' fill='#22c55e' /><path d='M7 12l4 4 6-7' stroke='white' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' /></svg>
                            ) : (
                              <svg width='22' height='22' viewBox='0 0 24 24' fill='none'><rect x='2' y='2' width='20' height='20' rx='4' fill='#ef4444' /><path d='M8 8l8 8M16 8l-8 8' stroke='white' strokeWidth='2.5' strokeLinecap='round' /></svg>
                            )}
                          </div>
                          <img className='sg-group-avatar' src={g.avatar} alt='' />
                          <div className='sg-group-info'>
                            <div className='sg-group-name'>{g.name}</div>
                            <div className='sg-group-meta'>
                              <span className='sg-group-followers'>{g.followers} עוקבים</span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className='sg-footer'>
              <button className='sg-cancel-btn'>ביטול</button>
              <button data-td='groups-save' className='sg-save-btn' disabled={selected === 0}>
                <svg width='18' height='18' viewBox='0 0 24 24' fill='none'><path d='M5 12l5 5L20 7' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round' /></svg>
                שמור ({selected} קבוצות)
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='modal-backdrop fade show'></div>
    </>
  )
}

// ── Connect-profile modal ─────────────────────────────────────────────────────
const ConnectModal: React.FC<{s: DemoState}> = ({s}) => (
  <>
    <div className='bottom-sheet-overlay show' style={{zIndex: 10002}} />
    <div className='td-connect-modal td-modal-in' data-td='connect-modal'>
      <button type='button' className='td-connect-close'>×</button>
      {s.connectStage === 'success' ? (
        <div className='wizard-step profile-step'>
          <div style={{padding: '24px 8px', textAlign: 'center'}}>
            <div style={{fontSize: 44, marginBottom: 8}}>✅</div>
            <h3 style={{margin: 0}}>התחברת בהצלחה!</h3>
            <p style={{margin: '6px 0 0', color: '#555'}}>הפרופיל חובר בהצלחה למערכת.</p>
          </div>
          <button data-td='connect-done' type='button' className='btn btn-success' style={{width: '100%', marginTop: 12}}>סיום</button>
        </div>
      ) : (
        <div className='wizard-step profile-step'>
          <div className='congrats-banner'>
            <div className='congrats-icon'>🎉</div>
            <h3>חבר את הפרופיל שלך!</h3>
            <p>התחבר עם המייל והסיסמה של הפייסבוק — בדיוק כמו התחברות רגילה.</p>
          </div>
          <div className='facebook-login-card'>
            <div className='fb-logo'><FbLogo /></div>
            <h2 className='fb-title'>התחבר לפייסבוק</h2>
            <button data-td='connect-password' className='wizard-btn primary' style={{display: 'block', width: '100%', textAlign: 'center'}}>
              <i className='bi bi-key-fill me-2'></i>
              התחבר עם שם משתמש וסיסמה
            </button>
          </div>
        </div>
      )}
    </div>
  </>
)

// ── Facebook in a browser window ──────────────────────────────────────────────
// What the customer sees after "התחבר עם שם משתמש וסיסמה": ordinary Facebook (Hebrew UI),
// where they log in, recover a password or approve the login exactly as they would anywhere.
const FB_URL: Record<DemoState['fbPage'], string> = {
  login: 'facebook.com/login',
  find: 'facebook.com/login/identify',
  method: 'facebook.com/recover/initiate',
  code: 'facebook.com/recover/code',
  newpass: 'facebook.com/recover/password',
  checkpoint: 'facebook.com/checkpoint',
}

const FbWordmark = ({size = 52}: {size?: number}) => <div className='td-fbw-wordmark' style={{fontSize: size}}>facebook</div>

const FbCard: React.FC<{title: string; children: React.ReactNode; footer: React.ReactNode}> = ({title, children, footer}) => (
  <div className='td-fbw-card td-pop'>
    <div className='td-fbw-card-title'>{title}</div>
    <div className='td-fbw-card-body'>{children}</div>
    <div className='td-fbw-card-footer'>{footer}</div>
  </div>
)

export const FacebookBrowser: React.FC<{s: DemoState}> = ({s}) => (
  <>
    <div className='td-fbw-backdrop' />
    <div className='td-fbw td-modal-in' dir='ltr'>
      <div className='td-fbw-chrome'>
        <div className='td-fbw-dots'><span /><span /><span /></div>
        <div className='td-fbw-tab'>
          <FbLogo size={14} /> Facebook
        </div>
      </div>
      <div className='td-fbw-bar'>
        <i className='bi bi-arrow-left'></i>
        <i className='bi bi-arrow-right'></i>
        <i className='bi bi-arrow-clockwise'></i>
        <div className='td-fbw-url'>
          <i className='bi bi-lock-fill'></i> {FB_URL[s.fbPage]}
        </div>
      </div>
      <div className='td-fbw-page' dir='rtl'>
        {s.fbPage === 'login' ? (
          <div className='td-fbw-login'>
            <FbWordmark />
            <div className='td-fbw-login-card'>
              <input data-td='fb-user' readOnly className={clsx('td-fbw-input', s.fbUser && 'td-focus')} placeholder='אימייל או מספר טלפון' value={s.fbUser} dir={s.fbUser ? 'ltr' : 'rtl'} />
              <input data-td='fb-pass' readOnly type='password' className={clsx('td-fbw-input', s.fbError && 'error')} placeholder='סיסמה' value={s.fbPass} />
              {s.fbError && (
                <div data-td='fb-error' className='td-fbw-error td-pop'>
                  הסיסמה שהזנת שגויה. <b>שכחת את הסיסמה?</b>
                </div>
              )}
              <button data-td='fb-login' className='td-fbw-btn big'>
                {s.fbBusy ? <span className='spinner-border spinner-border-sm'></span> : 'התחברות'}
              </button>
              <div data-td='fb-forgot' className='td-fbw-link'>שכחת את הסיסמה?</div>
              <div className='td-fbw-sep' />
              <button className='td-fbw-btn green'>יצירת חשבון חדש</button>
            </div>
          </div>
        ) : (
          <div className='td-fbw-flow'>
            <div className='td-fbw-topbar'><FbWordmark size={30} /></div>
            <div className='td-fbw-center'>
              {s.fbPage === 'find' && (
                <FbCard
                  title='מציאת החשבון שלך'
                  footer={<><button className='td-fbw-btn grey'>ביטול</button><button data-td='fb-find-search' className='td-fbw-btn'>חיפוש</button></>}
                >
                  <p>יש להזין את כתובת האימייל או את מספר הנייד שלך כדי לחפש את החשבון שלך.</p>
                  <input data-td='fb-find-input' readOnly className={clsx('td-fbw-input', s.fbFind && 'td-focus')} placeholder='אימייל או מספר נייד' value={s.fbFind} dir={s.fbFind ? 'ltr' : 'rtl'} />
                </FbCard>
              )}
              {s.fbPage === 'method' && (
                <FbCard
                  title='איפוס הסיסמה שלך'
                  footer={<><button className='td-fbw-btn grey'>זה לא אני</button><button data-td='fb-method-continue' className='td-fbw-btn'>המשך</button></>}
                >
                  <div className='td-fbw-method'>
                    <div>
                      <p style={{marginTop: 0}}>איך ברצונך לקבל את הקוד לאיפוס הסיסמה?</p>
                      <label className='td-fbw-radio on'><span className='dot' /> <span><b>שליחת קוד באמצעות SMS</b><br /><span dir='ltr'>+972 ••• ••• ••67</span></span></label>
                      <label className='td-fbw-radio'><span className='dot' /> <span><b>שליחת קוד באמצעות אימייל</b><br /><span dir='ltr'>d••••••@gmail.com</span></span></label>
                    </div>
                    <div className='td-fbw-who'>
                      <img src={DEMO_PROFILES[0].avatar} alt='' />
                      <b>{DEMO_PROFILES[0].name}</b>
                      <span>משתמש פייסבוק</span>
                    </div>
                  </div>
                </FbCard>
              )}
              {s.fbPage === 'code' && (
                <FbCard
                  title='הזנת קוד אבטחה'
                  footer={<><button className='td-fbw-btn grey'>ביטול</button><button data-td='fb-code-continue' className='td-fbw-btn'>המשך</button></>}
                >
                  <p>יש לבדוק אם קיבלת בטלפון הודעת טקסט עם הקוד. הקוד מורכב מ-6 ספרות.</p>
                  <div style={{display: 'flex', alignItems: 'center', gap: 14}}>
                    <input data-td='fb-code-input' readOnly className={clsx('td-fbw-input', s.fbCode && 'td-focus')} style={{width: 180, marginBottom: 0, letterSpacing: 4}} placeholder='הזנת הקוד' value={s.fbCode} dir='ltr' />
                    <span style={{fontSize: 13, color: '#65676b'}}>שלחנו לך קוד ל:<br /><span dir='ltr'>+972 ••• ••• ••67</span></span>
                  </div>
                </FbCard>
              )}
              {s.fbPage === 'newpass' && (
                <FbCard
                  title='בחירת סיסמה חדשה'
                  footer={<><button className='td-fbw-btn grey'>דילוג</button><button data-td='fb-newpass-continue' className='td-fbw-btn'>המשך</button></>}
                >
                  <p>יש ליצור סיסמה חדשה באורך של שישה תווים לפחות. סיסמה חזקה משלבת אותיות, מספרים וסימנים.</p>
                  <input data-td='fb-newpass-input' readOnly type='password' className={clsx('td-fbw-input', s.fbNewPass && 'td-focus')} style={{width: 300}} placeholder='סיסמה חדשה' value={s.fbNewPass} />
                </FbCard>
              )}
              {s.fbPage === 'checkpoint' && (
                <div data-td='fb-checkpoint'>
                  <FbCard
                    title='יש לבדוק את ההתראות במכשיר אחר'
                    footer={<button className='td-fbw-btn grey'>לנסות דרך אחרת</button>}
                  >
                    <div className='td-fbw-check'>
                      <i className='bi bi-phone-vibrate'></i>
                      <div>
                        <p style={{marginTop: 0}}>שלחנו התראה ל-iPhone שלך. יש לפתוח את ההתראות בפייסבוק שם ולאשר שזה אתה.</p>
                        <div className='td-fbw-wait'>
                          <span className='spinner-border spinner-border-sm'></span> ממתינים לאישור...
                        </div>
                      </div>
                    </div>
                  </FbCard>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  </>
)

// ── The customer's phone: SMS code and the "is this you?" approval ────────────
export const PhoneMock: React.FC<{s: DemoState}> = ({s}) => (
  <div data-td='phone' className='td-phone td-phone-in' dir='rtl'>
    <div className='td-phone-screen'>
      <div className='td-phone-status' dir='ltr'>
        <span>9:41</span>
        <span><i className='bi bi-reception-4'></i> <i className='bi bi-wifi'></i> <i className='bi bi-battery-full'></i></span>
      </div>
      {s.phone === 'sms' && (
        <>
          <div className='td-phone-clock'>9:41</div>
          <div className='td-phone-notif td-drop'>
            <div className='td-phone-notif-app'><i className='bi bi-chat-fill' style={{color: '#34c759'}}></i> הודעות · עכשיו</div>
            <b>FACEBOOK</b>
            <div>‏482913 הוא קוד איפוס הסיסמה שלך בפייסבוק</div>
          </div>
        </>
      )}
      {(s.phone === 'approve' || s.phone === 'approved') && (
        <div className='td-phone-app td-pop'>
          <div className='td-phone-app-top'><FbLogo size={26} /></div>
          {s.phone === 'approve' ? (
            <div className='td-phone-approve'>
              <i className='bi bi-shield-lock-fill'></i>
              <h5>האם זה אתה שמנסה להתחבר?</h5>
              <p>Chrome · Windows<br />תל אביב, ישראל · עכשיו</p>
              <button data-td='phone-yes' className='td-fbw-btn'>כן, זה אני</button>
              <button className='td-fbw-btn grey'>לא, זה לא אני</button>
            </div>
          ) : (
            <div className='td-phone-approve'>
              <i className='bi bi-check-circle-fill' style={{color: '#31a24c'}}></i>
              <h5>ההתחברות אושרה</h5>
              <p>אפשר לחזור למחשב.</p>
            </div>
          )}
        </div>
      )}
    </div>
  </div>
)
